import { X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  Switch,
  toast,
} from '@kmgestao/ui';

import {
  PLAN_ASAAS_BILLING_TYPES,
  PLAN_ASAAS_BILLING_TYPE_LABELS,
  PLAN_BILLING_CYCLES,
  PLAN_BILLING_CYCLE_LABELS,
  PLAN_TIERS,
  buildPlanFormSchema,
  formToRpcPayload,
  useUpsertPlan,
  type PlanFormInput,
  type PlanRow,
} from '../../../../features/plans-admin/index.js';

/** Modal de criação/edição de plano. Em modo edição, `editing`
 * preenchido hidrata o form. Em criação, `editing === null` e abre
 * com defaults (tier=custom, is_public=true, etc). O campo `key`
 * fica readonly em modo edição — alterar `key` quebraria FKs em
 * proposals/subscriptions. */
export function PlanFormDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: PlanRow | null;
}) {
  const isEditing = editing !== null;
  const upsertMutation = useUpsertPlan();

  const [values, setValues] = useState<PlanFormInput>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [featureDraft, setFeatureDraft] = useState('');

  // Hidrata quando abre em modo edição; reseta pra emptyForm quando
  // abre em modo criação. Dependência em `open` + `editing` cobre
  // os dois casos.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setValues({
        key: editing.key,
        name_pt_br: editing.name_pt_br,
        description: editing.description ?? '',
        tier: editing.tier,
        monthly_price_brl:
          editing.monthly_price_brl === null ? '' : String(editing.monthly_price_brl),
        yearly_price_brl: editing.yearly_price_brl === null ? '' : String(editing.yearly_price_brl),
        trial_days: String(editing.trial_days),
        max_users: editing.max_users === null ? '' : String(editing.max_users),
        max_patients: editing.max_patients === null ? '' : String(editing.max_patients),
        max_storage_gb: editing.max_storage_gb === null ? '' : String(editing.max_storage_gb),
        max_ai_tokens_monthly:
          editing.max_ai_tokens_monthly === null ? '' : String(editing.max_ai_tokens_monthly),
        features: editing.features,
        is_public: editing.is_public,
        is_active: editing.is_active,
        sort_order: String(editing.sort_order),
        billing_cycle: editing.billing_cycle,
        asaas_default_billing_type: editing.asaas_default_billing_type,
      });
    } else {
      setValues(emptyForm());
    }
    setErrors({});
    setFeatureDraft('');
  }, [open, editing]);

  const update = <K extends keyof PlanFormInput>(key: K, value: PlanFormInput[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const addFeature = () => {
    const trimmed = featureDraft.trim();
    if (!trimmed) return;
    if (values.features.includes(trimmed)) {
      toast.error('Essa feature já está na lista.');
      return;
    }
    update('features', [...values.features, trimmed]);
    setFeatureDraft('');
  };

  const removeFeature = (feat: string) => {
    update(
      'features',
      values.features.filter((f) => f !== feat),
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const schema = buildPlanFormSchema({ isEditing });
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string' && !fieldErrors[path]) {
          fieldErrors[path] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }
    try {
      await upsertMutation.mutateAsync(formToRpcPayload(parsed.data));
      toast.success(isEditing ? 'Plano atualizado.' : 'Plano criado.');
      onOpenChange(false);
    } catch (err) {
      toast.error('Não foi possível salvar', {
        description: err instanceof Error ? err.message : '',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? `Editar plano: ${editing.name_pt_br}` : 'Novo plano'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {/* Identidade */}
          <Section title="Identidade">
            <Field
              label="Chave técnica *"
              error={errors['key']}
              help="Só minúsculas, dígitos e underscore. Ex: pro_plus. Imutável após criação."
            >
              <Input
                value={values.key}
                onChange={(e) => update('key', e.target.value)}
                disabled={isEditing || upsertMutation.isPending}
                placeholder="pro_plus"
                aria-invalid={errors['key'] ? true : undefined}
              />
            </Field>
            <Field label="Nome (pt-BR) *" error={errors['name_pt_br']}>
              <Input
                value={values.name_pt_br}
                onChange={(e) => update('name_pt_br', e.target.value)}
                disabled={upsertMutation.isPending}
                placeholder="Pro Plus"
                aria-invalid={errors['name_pt_br'] ? true : undefined}
              />
            </Field>
            <Field label="Descrição" error={errors['description']}>
              <textarea
                value={values.description}
                onChange={(e) => update('description', e.target.value)}
                disabled={upsertMutation.isPending}
                rows={3}
                placeholder="Descrição comercial mostrada nas propostas e futuramente na landing /planos."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
              />
            </Field>
            <Field
              label="Tier *"
              help="Categoria comercial. Usado em analytics e em regras de negócio."
            >
              <Select
                value={values.tier}
                onChange={(e) => update('tier', e.target.value as PlanFormInput['tier'])}
                disabled={upsertMutation.isPending}
              >
                {PLAN_TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </Select>
            </Field>
          </Section>

          {/* Preços */}
          <Section title="Preços & Trial">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Preço mensal (R$)"
                error={errors['monthly_price_brl']}
                help="Vazio = sob consulta."
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  value={values.monthly_price_brl}
                  onChange={(e) => update('monthly_price_brl', e.target.value)}
                  disabled={upsertMutation.isPending}
                  placeholder="299.00"
                />
              </Field>
              <Field
                label="Preço anual (R$)"
                error={errors['yearly_price_brl']}
                help="Vazio = sem opção anual."
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  value={values.yearly_price_brl}
                  onChange={(e) => update('yearly_price_brl', e.target.value)}
                  disabled={upsertMutation.isPending}
                  placeholder="2990.00"
                />
              </Field>
            </div>
            <Field
              label="Dias de trial"
              error={errors['trial_days']}
              help="0 = sem trial. Ex: 14 para Pro."
            >
              <Input
                type="number"
                min="0"
                value={values.trial_days}
                onChange={(e) => update('trial_days', e.target.value)}
                disabled={upsertMutation.isPending}
              />
            </Field>
          </Section>

          {/* Cobrança Asaas */}
          <Section
            title="Cobrança (Asaas)"
            description="Como a assinatura será criada no Asaas quando o contrato for assinado e a clínica provisionada."
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Ciclo de cobrança"
                help="Frequência que o Asaas gera a próxima fatura automaticamente."
              >
                <Select
                  value={values.billing_cycle}
                  onChange={(e) =>
                    update('billing_cycle', e.target.value as PlanFormInput['billing_cycle'])
                  }
                  disabled={upsertMutation.isPending}
                >
                  {PLAN_BILLING_CYCLES.map((cycle) => (
                    <option key={cycle} value={cycle}>
                      {PLAN_BILLING_CYCLE_LABELS[cycle]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Método de pagamento padrão"
                help="UNDEFINED deixa o cliente escolher no checkout Asaas (mais flexível)."
              >
                <Select
                  value={values.asaas_default_billing_type}
                  onChange={(e) =>
                    update(
                      'asaas_default_billing_type',
                      e.target.value as PlanFormInput['asaas_default_billing_type'],
                    )
                  }
                  disabled={upsertMutation.isPending}
                >
                  {PLAN_ASAAS_BILLING_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {PLAN_ASAAS_BILLING_TYPE_LABELS[t]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Section>

          {/* Limites */}
          <Section title="Limites" description="Vazio em qualquer campo = ilimitado.">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Max. usuários" error={errors['max_users']}>
                <Input
                  type="number"
                  min="1"
                  value={values.max_users}
                  onChange={(e) => update('max_users', e.target.value)}
                  disabled={upsertMutation.isPending}
                />
              </Field>
              <Field label="Max. pacientes" error={errors['max_patients']}>
                <Input
                  type="number"
                  min="1"
                  value={values.max_patients}
                  onChange={(e) => update('max_patients', e.target.value)}
                  disabled={upsertMutation.isPending}
                />
              </Field>
              <Field label="Storage (GB)" error={errors['max_storage_gb']}>
                <Input
                  type="number"
                  min="1"
                  value={values.max_storage_gb}
                  onChange={(e) => update('max_storage_gb', e.target.value)}
                  disabled={upsertMutation.isPending}
                />
              </Field>
              <Field label="Tokens IA/mês" error={errors['max_ai_tokens_monthly']}>
                <Input
                  type="number"
                  min="0"
                  value={values.max_ai_tokens_monthly}
                  onChange={(e) => update('max_ai_tokens_monthly', e.target.value)}
                  disabled={upsertMutation.isPending}
                />
              </Field>
            </div>
          </Section>

          {/* Features */}
          <Section
            title="Features"
            description="Lista de capacidades incluídas. Aparece como badges na proposta."
          >
            <div className="flex gap-2">
              <Input
                value={featureDraft}
                onChange={(e) => setFeatureDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addFeature();
                  }
                }}
                disabled={upsertMutation.isPending}
                placeholder="ex: agenda, prontuario, ia_avancada"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addFeature}
                disabled={upsertMutation.isPending || featureDraft.trim().length === 0}
              >
                Adicionar
              </Button>
            </div>
            {values.features.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {values.features.map((feat) => (
                  <span
                    key={feat}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs font-medium text-foreground"
                  >
                    {feat}
                    <button
                      type="button"
                      onClick={() => removeFeature(feat)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remover ${feat}`}
                      disabled={upsertMutation.isPending}
                    >
                      <X aria-hidden="true" className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhuma feature adicionada.</p>
            )}
          </Section>

          {/* Visibilidade & Ordem */}
          <Section title="Visibilidade">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="plan-is-active">Ativo</Label>
                <p className="text-xs text-muted-foreground">
                  Planos inativos não aparecem no dropdown de novas propostas.
                </p>
              </div>
              <Switch
                id="plan-is-active"
                checked={values.is_active}
                onCheckedChange={(v) => update('is_active', v)}
                disabled={upsertMutation.isPending}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="plan-is-public">Público</Label>
                <p className="text-xs text-muted-foreground">
                  Se desligado, o plano existe mas só é usável via negociação direta (ex:
                  Enterprise).
                </p>
              </div>
              <Switch
                id="plan-is-public"
                checked={values.is_public}
                onCheckedChange={(v) => update('is_public', v)}
                disabled={upsertMutation.isPending}
              />
            </div>
            <Field
              label="Ordem de exibição"
              error={errors['sort_order']}
              help="Menor = aparece primeiro. Default 100."
            >
              <Input
                type="number"
                value={values.sort_order}
                onChange={(e) => update('sort_order', e.target.value)}
                disabled={upsertMutation.isPending}
              />
            </Field>
          </Section>

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={upsertMutation.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={upsertMutation.isPending}>
              {upsertMutation.isPending
                ? 'Salvando…'
                : isEditing
                  ? 'Salvar alterações'
                  : 'Criar plano'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="space-y-3 rounded-lg border border-border/60 p-4">
      <legend className="px-1 text-sm font-semibold text-foreground">{title}</legend>
      {description ? <p className="-mt-1 text-xs text-muted-foreground">{description}</p> : null}
      {children}
    </fieldset>
  );
}

function Field({
  label,
  children,
  error,
  help,
}: {
  label: string;
  children: React.ReactNode;
  error?: string | undefined;
  help?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : help ? (
        <p className="text-xs text-muted-foreground">{help}</p>
      ) : null}
    </div>
  );
}

function emptyForm(): PlanFormInput {
  return {
    key: '',
    name_pt_br: '',
    description: '',
    tier: 'custom',
    monthly_price_brl: '',
    yearly_price_brl: '',
    trial_days: '0',
    max_users: '',
    max_patients: '',
    max_storage_gb: '',
    max_ai_tokens_monthly: '',
    features: [],
    is_public: true,
    is_active: true,
    sort_order: '100',
    billing_cycle: 'monthly',
    asaas_default_billing_type: 'UNDEFINED',
  };
}
